import Vue from 'vue';
import axios from 'axios';
import User from '@models/User';
import Storage from '@classes/Storage';
import { USER_ENDPOINT } from '@constants/api';
import {
    TOKEN_NAME,
    REFRESH_TOKEN_NAME,
} from '@constants/token';
import _isEmpty from 'lodash/isEmpty';

export const namespace = true;

export const state = {
    model: new User(),
    isAuthenticated: false,
};

export const getters = {
    model: state => state.model,
    isAuthenticated: state => state.isAuthenticated,
};

export const mutations = {
    /**
     * Mutate the state of the user class instance,
     *
     * @param  {Object} state      The Vuex state container.
     * @param  {Object} user       The user object from API request.
     */
    set: (state, user) => {
        state.model = _isEmpty(user) ? new User() : new User(user);
    },

    /**
     * Mutate the state of the user's auth state,
     *
     * @param  {Object} state      The Vuex state container.
     * @param  {Boolean} data      Whether or not the user is logged in.
     */
    setAuthenticationStatus: (state, isAuthenticated = false) => {
        state.isAuthenticated = isAuthenticated;
    },
};

export const actions = {
    /**
     * This is automatically run in `src/state/store.js` when the app starts, along with any other actions named `init` in other modules.
     *
     * @param {Function} options.dispatch The Vuex state dispatch function for performing actions.
     */
    init ({ dispatch }) {
        Vue.nextTick(() => dispatch('fetch'));
    },

    /**
     * The dispatchable action to fetch and commit the current user's data.
     *
     * @param {Function} options.commit The Vuex state commit function.
     * @param {Object}   data           The page user object from API request.
     *
     * @return Promise
     */
    async fetch ({ dispatch, state }) {
        try {
            const { data: user } = await axios.get(USER_ENDPOINT);

            dispatch('login', user);

            return Promise.resolve(state.model);
        } catch (error) {
            return Promise.reject(error);
        }
    },

    /**
     * User login.
     *
     * @param  {Function} options.commit The Vuex state commit function.
     * @param  {User}     user           The user model
     */
    login ({ commit }, user) {
        if (user) {
            commit('set', user);
            commit('setAuthenticationStatus', true);
        }
    },

    /**
     * User logout.
     *
     * @param {Function} options.commit The Vuex state commit function.
     */
    logout ({ commit }) {
        new Storage().remove([TOKEN_NAME, REFRESH_TOKEN_NAME]);

        commit('set', null);
        commit('setAuthenticationStatus', false);
    },
};
